import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface DashboardTreatment {
  id: string
  treatment_type: string
  treatment_date: string
  patient_name: string
  patient_species: string
  owner_name: string
  veterinarian_name: string | null
}

export interface DashboardRefill {
  id: string
  remaining_refills: number
  start_date: string
  end_date: string | null
  patient_name: string
  medication_name: string
  last_filled_date: string | null
}

export interface DashboardAlert {
  id: string
  alert_type: string
  severity: string
  description: string
  conflicting_drugs: string
  prescription_id: string
  patient_name: string
}

export interface DashboardLabResult {
  id: string
  test_name: string
  date_ordered: string
  status: string
  patient_name: string
}

interface DashboardState {
  todaysTreatments: DashboardTreatment[]
  pendingRefills: DashboardRefill[]
  drugAlerts: DashboardAlert[]
  labResultsAwaitingReview: DashboardLabResult[]
  loading: boolean
  error: string | null
}

const initialState: DashboardState = {
  todaysTreatments: [],
  pendingRefills: [],
  drugAlerts: [],
  labResultsAwaitingReview: [],
  loading: false,
  error: null,
}

export const fetchDashboard = createAsyncThunk('dashboard/fetch', async () => {
  const res = await fetch('/.netlify/functions/dashboard')
  if (!res.ok) throw new Error('Failed to fetch dashboard data')
  return res.json() as Promise<{
    todaysTreatments: DashboardTreatment[]
    pendingRefills: DashboardRefill[]
    drugAlerts: DashboardAlert[]
    labResultsAwaitingReview: DashboardLabResult[]
  }>
})

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        if (
          state.todaysTreatments.length === 0 &&
          state.pendingRefills.length === 0 &&
          state.drugAlerts.length === 0 &&
          state.labResultsAwaitingReview.length === 0
        ) {
          state.loading = true
        }
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.todaysTreatments = action.payload.todaysTreatments
        state.pendingRefills = action.payload.pendingRefills
        state.drugAlerts = action.payload.drugAlerts
        state.labResultsAwaitingReview = action.payload.labResultsAwaitingReview
        state.loading = false
        state.error = null
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load dashboard'
      })
  },
})

export default dashboardSlice.reducer
